/**
 * WomensHealthStep.js — Assessment Wizard, Screen 11 "Women's Health"
 *
 * Captures pregnancy/breastfeeding context that materially changes
 * nutrient targets (FSD Section 4, Screen 11). Built on the shared
 * AssessmentStepShell so progress bar, step counter, question header, and
 * Previous/Next footer stay identical to every other wizard screen.
 *
 * This component assumes it is only ever mounted when Gender = Female —
 * the wizard (DietPlannerWizard.js) owns that gate and skips straight
 * from Meal Schedule (10) to Summary (12) for non-female users, exactly
 * as FSD Section 5's Conditional Display Logic specifies. This file does
 * not re-check gender.
 *
 * Field semantics (per FSD):
 *  - Conditions (Pregnant/Breastfeeding/PCOS/Menopause) → required
 *                             multi-select, at least one or "None".
 *  - Trimester               → required only if "Pregnant" is selected.
 *  - Due Date                → optional, shown only if "Pregnant".
 *  - Doctor-Advised
 *    Restrictions             → optional free text, shown only if "Pregnant".
 *  - Gestational Diabetes    → required only if "Pregnant"; if "Yes",
 *                             applies the same glycemic-load restrictions
 *                             as Diabetes (Screen 6), on top of pregnancy
 *                             rules — that merge happens in the Rule
 *                             Engine downstream, not in this component.
 */

import React, { useState, useCallback, useMemo } from "react";
import AssessmentStepShell from "../Assesment/AssessmentStepShell";
import useWomensHealthAnalytics from "./useWomensHealthAnalytics";
import { validateWomensHealth, toggleCondition } from "./WomensHealthStep.validation";
import { TOTAL_STEPS, STEP_NUMBERS } from "../Assesment/assessmentConfig";
import {
  CONDITION_OPTIONS,
  NONE_OPTION_ID,
  TRIMESTER_OPTIONS,
  YES_NO_OPTIONS,
  WOMENS_HEALTH_INITIAL_DATA,
} from "./WomensHealthStep.constants";
import {
  FieldGroup,
  FieldLabel,
  RequiredMark,
  ErrorText,
  ConditionGrid,
  ConditionCard,
  PregnancyPanel,
  FieldRow,
  Select,
  DateInput,
  TextArea,
  ToggleRow,
  ToggleButton,
} from "./WomensHealthStep.styles";

const STEP_NUMBER = STEP_NUMBERS.WOMENS_HEALTH;

/**
 * @param {object}   props
 * @param {string}   props.sessionId     - Anonymous session id from Screen 1.
 * @param {object}   [props.initialData] - Previously entered values, if the
 *   user navigated back to this step.
 * @param {function} props.onPrevious    - Go to Screen 10 (Meal Schedule).
 * @param {function} props.onNext        - Go to Screen 12 (Summary), receives
 *   the full women's-health data object ({ conditions, trimester, dueDate,
 *   doctorAdvisedRestrictions, gestationalDiabetes }).
 */
const WomensHealthStep = ({ sessionId, initialData = null, onPrevious, onNext }) => {
  const analytics = useWomensHealthAnalytics();
  const [formData, setFormData] = useState({ ...WOMENS_HEALTH_INITIAL_DATA, ...initialData });

  const { isValid, errors } = useMemo(() => validateWomensHealth(formData), [formData]);
  const isPregnant = formData.conditions.includes("pregnant");

  const handleConditionClick = useCallback(
    (optionId) => {
      setFormData((prev) => {
        const conditions = toggleCondition(prev.conditions, optionId);
        // Clear pregnancy-only fields the moment "Pregnant" is deselected,
        // so stale answers can't be submitted silently.
        const next = conditions.includes("pregnant")
          ? { ...prev, conditions }
          : {
              ...prev,
              conditions,
              trimester: "",
              dueDate: "",
              doctorAdvisedRestrictions: "",
              gestationalDiabetes: null,
            };
        analytics.trackConditionToggle(sessionId, conditions);
        return next;
      });
    },
    [analytics, sessionId]
  );

  const handleTrimesterChange = useCallback(
    (e) => {
      const value = e.target.value;
      setFormData((prev) => ({ ...prev, trimester: value }));
      analytics.trackTrimesterSelect(sessionId, value);
    },
    [analytics, sessionId]
  );

  const handleDueDateChange = useCallback((e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, dueDate: value }));
  }, []);

  const handleRestrictionsChange = useCallback((e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, doctorAdvisedRestrictions: value }));
  }, []);

  const handleGestationalDiabetesClick = useCallback(
    (value) => {
      setFormData((prev) => ({ ...prev, gestationalDiabetes: value }));
      analytics.trackGestationalDiabetesSelect(sessionId, value);
    },
    [analytics, sessionId]
  );

  const handlePrevious = useCallback(() => {
    // Previous from Women's Health (Screen 11) returns to Meal Schedule (Screen 10).
    analytics.trackStepPrevious(sessionId);
    onPrevious?.();
  }, [analytics, sessionId, onPrevious]);

  const handleNext = useCallback(() => {
    if (!isValid) {
      // Defensive guard — Next is already disabled via nextDisabled below,
      // this only fires if something calls onNext programmatically.
      analytics.trackValidationBlocked(sessionId, errors);
      return;
    }

    analytics.trackStepNext(sessionId, formData);
    // Advances the wizard to Screen 12 (Summary).
    onNext?.(formData);
  }, [analytics, sessionId, formData, isValid, errors, onNext]);

  return (
    <AssessmentStepShell
      step={STEP_NUMBER}
      totalSteps={TOTAL_STEPS}
      title="A few things specific to you"
      description="This helps us adjust your calorie and micronutrient targets safely."
      onPrevious={handlePrevious}
      onNext={handleNext}
      nextDisabled={!isValid}
    >
      <FieldGroup>
        <FieldLabel id="conditionsLabel">
          Do any of these apply to you right now?
          <RequiredMark>*</RequiredMark>
        </FieldLabel>
        <ConditionGrid role="group" aria-labelledby="conditionsLabel">
          {CONDITION_OPTIONS.map((opt) => {
            const active = formData.conditions.includes(opt.id);
            return (
              <ConditionCard
                key={opt.id}
                type="button"
                aria-pressed={active}
                $active={active}
                onClick={() => handleConditionClick(opt.id)}
              >
                {opt.label}
              </ConditionCard>
            );
          })}
          <ConditionCard
            type="button"
            aria-pressed={formData.conditions.includes(NONE_OPTION_ID)}
            $active={formData.conditions.includes(NONE_OPTION_ID)}
            onClick={() => handleConditionClick(NONE_OPTION_ID)}
          >
            None of these
          </ConditionCard>
        </ConditionGrid>
        {errors.conditions && <ErrorText>{errors.conditions}</ErrorText>}
      </FieldGroup>

      {isPregnant && (
        <PregnancyPanel>
          <FieldRow>
            <div>
              <FieldLabel htmlFor="trimester">
                Trimester
                <RequiredMark>*</RequiredMark>
              </FieldLabel>
              <Select
                id="trimester"
                value={formData.trimester}
                onChange={handleTrimesterChange}
                $error={Boolean(errors.trimester)}
              >
                {TRIMESTER_OPTIONS.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </Select>
              {errors.trimester && <ErrorText>{errors.trimester}</ErrorText>}
            </div>
            <div>
              <FieldLabel htmlFor="dueDate">Due date (optional)</FieldLabel>
              <DateInput id="dueDate" value={formData.dueDate} onChange={handleDueDateChange} />
            </div>
          </FieldRow>

          <FieldGroup>
            <FieldLabel htmlFor="doctorAdvisedRestrictions">
              Doctor-advised restrictions (optional)
            </FieldLabel>
            <TextArea
              id="doctorAdvisedRestrictions"
              value={formData.doctorAdvisedRestrictions}
              onChange={handleRestrictionsChange}
              placeholder="e.g. avoid raw dairy, limit caffeine..."
              maxLength={200}
            />
          </FieldGroup>

          <FieldGroup>
            <FieldLabel id="gdLabel">
              Gestational diabetes
              <RequiredMark>*</RequiredMark>
            </FieldLabel>
            <ToggleRow role="group" aria-labelledby="gdLabel">
              {YES_NO_OPTIONS.map((opt) => {
                const active = formData.gestationalDiabetes === opt.id;
                return (
                  <ToggleButton
                    key={opt.id}
                    type="button"
                    aria-pressed={active}
                    $active={active}
                    onClick={() => handleGestationalDiabetesClick(opt.id)}
                  >
                    {opt.label}
                  </ToggleButton>
                );
              })}
            </ToggleRow>
            {errors.gestationalDiabetes && <ErrorText>{errors.gestationalDiabetes}</ErrorText>}
          </FieldGroup>
        </PregnancyPanel>
      )}
    </AssessmentStepShell>
  );
};

WomensHealthStep.displayName = "WomensHealthStep";
export default React.memo(WomensHealthStep);
